import { useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import {
  Loader2, Send, Hash, Plus, Trash2, Image as ImageIcon, Layers, MessageSquare, AtSign,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

const discordSendMessage = httpsCallable(functions, 'discord_sendMessage');

interface DiscordChannel {
  id: string;
  name: string;
  type: number;
  parent_id?: string;
  position: number;
}

interface EmbedField {
  name: string;
  value: string;
  inline: boolean;
}

const PRESET_COLORS: { label: string; hex: string }[] = [
  { label: 'Dorado', hex: '#facc15' },
  { label: 'Rojo', hex: '#ef4444' },
  { label: 'Verde', hex: '#22c55e' },
  { label: 'Azul', hex: '#3b82f6' },
  { label: 'Morado', hex: '#9b59b6' },
  { label: 'Naranja', hex: '#f97316' },
  { label: 'Blanco', hex: '#ffffff' },
];

function hexToInt(hex: string): number {
  return parseInt(hex.replace('#', ''), 16) || 0;
}

export function DiscordTemplateComposer({
  channels,
  disabled,
}: {
  channels: DiscordChannel[];
  disabled?: boolean;
}) {
  const { toast } = useToast();

  // Destino
  const textChannels = useMemo(
    () => channels.filter(c => c.type === 0 || c.type === 5).sort((a, b) => a.position - b.position),
    [channels]
  );
  const [target, setTarget] = useState<'channel' | 'dm'>('channel');
  const [channelId, setChannelId] = useState<string>('');
  const [userId, setUserId] = useState('');

  // Mensaje
  const [content, setContent] = useState('');

  // Embed
  const [useEmbed, setUseEmbed] = useState(false);
  const [title, setTitle] = useState('');
  const [titleUrl, setTitleUrl] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#facc15');
  const [authorName, setAuthorName] = useState('');
  const [authorIcon, setAuthorIcon] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [footerText, setFooterText] = useState('');
  const [footerIcon, setFooterIcon] = useState('');
  const [withTimestamp, setWithTimestamp] = useState(false);
  const [fields, setFields] = useState<EmbedField[]>([]);

  const [sending, setSending] = useState(false);

  const selectedChannel = textChannels.find(c => c.id === channelId);

  const addField = () => setFields(prev => [...prev, { name: '', value: '', inline: false }]);
  const updateField = (i: number, patch: Partial<EmbedField>) =>
    setFields(prev => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeField = (i: number) => setFields(prev => prev.filter((_, idx) => idx !== i));

  const buildEmbed = () => {
    if (!useEmbed) return null;
    const e: Record<string, unknown> = {};
    if (title.trim()) e.title = title.trim();
    if (titleUrl.trim()) e.url = titleUrl.trim();
    if (description.trim()) e.description = description.trim();
    if (color) e.color = hexToInt(color);
    if (authorName.trim()) e.author = { name: authorName.trim(), ...(authorIcon.trim() ? { icon_url: authorIcon.trim() } : {}) };
    if (footerText.trim()) e.footer = { text: footerText.trim(), ...(footerIcon.trim() ? { icon_url: footerIcon.trim() } : {}) };
    if (thumbnailUrl.trim()) e.thumbnail = { url: thumbnailUrl.trim() };
    if (imageUrl.trim()) e.image = { url: imageUrl.trim() };
    if (withTimestamp) e.timestamp = true;
    const cleanFields = fields.filter(f => f.name.trim() || f.value.trim());
    if (cleanFields.length > 0) e.fields = cleanFields;
    return Object.keys(e).length > 0 ? e : null;
  };

  const previewEmbed = buildEmbed();

  const canSend =
    !disabled &&
    !sending &&
    (target === 'channel' ? !!channelId : !!userId.trim()) &&
    (content.trim().length > 0 || !!previewEmbed);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await discordSendMessage({
        channelId: target === 'channel' ? channelId : undefined,
        userId: target === 'dm' ? userId.trim() : undefined,
        content: content.trim() || undefined,
        embed: buildEmbed(),
      });
      toast({
        title: 'Mensaje enviado',
        description: target === 'channel'
          ? `Enviado a #${selectedChannel?.name ?? 'canal'}`
          : 'DM enviado al usuario',
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      toast({ variant: 'destructive', title: 'No se pudo enviar', description: msg });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex gap-4 flex-1 min-h-0">
      {/* ── Formulario ── */}
      <ScrollArea className="flex-1 min-h-0 pr-3">
        <div className="space-y-5 pb-4">

          {/* Destino */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 rounded-xl border border-white/8 bg-muted/30 p-1 w-fit">
              <button
                onClick={() => setTarget('channel')}
                className={cn('flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                  target === 'channel' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground')}
              >
                <Hash className="h-3.5 w-3.5" /> Canal
              </button>
              <button
                onClick={() => setTarget('dm')}
                className={cn('flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                  target === 'dm' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground')}
              >
                <AtSign className="h-3.5 w-3.5" /> Mensaje directo
              </button>
            </div>

            {target === 'channel' ? (
              <Select value={channelId} onValueChange={setChannelId} disabled={disabled}>
                <SelectTrigger className="rounded-xl border-white/10 bg-muted/30">
                  <SelectValue placeholder="Selecciona un canal de texto…" />
                </SelectTrigger>
                <SelectContent>
                  {textChannels.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" />{c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={userId}
                onChange={e => setUserId(e.target.value)}
                placeholder="ID del usuario de Discord (ej: 123456789012345678)"
                className="rounded-xl border-white/10 bg-muted/30"
                disabled={disabled}
              />
            )}
          </div>

          {/* Mensaje de texto */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
              <MessageSquare className="h-3.5 w-3.5" /> Mensaje
            </Label>
            <Textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Texto del mensaje (opcional si añades un embed)…"
              className="min-h-[90px] rounded-xl border-white/10 bg-muted/30 text-sm"
              disabled={disabled}
            />
          </div>

          {/* Embed toggle */}
          <div className="flex items-center justify-between rounded-xl border border-white/8 bg-muted/30 px-4 py-3">
            <Label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <Layers className="h-4 w-4 text-primary" /> Añadir embed
            </Label>
            <Switch checked={useEmbed} onCheckedChange={setUseEmbed} disabled={disabled} />
          </div>

          {/* Embed builder */}
          {useEmbed && (
            <div className="space-y-4 rounded-xl border border-white/8 bg-card/40 p-4">
              {/* Color */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground/70">Color de la barra</Label>
                <div className="flex flex-wrap items-center gap-2">
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c.hex}
                      onClick={() => setColor(c.hex)}
                      title={c.label}
                      className={cn('h-7 w-7 rounded-full border-2 transition-transform hover:scale-110',
                        color.toLowerCase() === c.hex.toLowerCase() ? 'border-white' : 'border-white/20')}
                      style={{ backgroundColor: c.hex }}
                    />
                  ))}
                  <input
                    type="color"
                    value={color}
                    onChange={e => setColor(e.target.value)}
                    className="h-7 w-9 cursor-pointer rounded border border-white/20 bg-transparent p-0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/70">Título</Label>
                  <Input value={title} onChange={e => setTitle(e.target.value)} className="rounded-lg border-white/10 bg-muted/30" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/70">Enlace del título (URL)</Label>
                  <Input value={titleUrl} onChange={e => setTitleUrl(e.target.value)} placeholder="https://…" className="rounded-lg border-white/10 bg-muted/30" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground/70">Descripción</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} className="min-h-[80px] rounded-lg border-white/10 bg-muted/30 text-sm" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/70">Autor (nombre)</Label>
                  <Input value={authorName} onChange={e => setAuthorName(e.target.value)} className="rounded-lg border-white/10 bg-muted/30" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/70">Autor (icono URL)</Label>
                  <Input value={authorIcon} onChange={e => setAuthorIcon(e.target.value)} placeholder="https://…" className="rounded-lg border-white/10 bg-muted/30" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/70">Miniatura (URL)</Label>
                  <Input value={thumbnailUrl} onChange={e => setThumbnailUrl(e.target.value)} placeholder="https://…" className="rounded-lg border-white/10 bg-muted/30" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/70">Imagen grande (URL)</Label>
                  <Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://…" className="rounded-lg border-white/10 bg-muted/30" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/70">Pie de página (texto)</Label>
                  <Input value={footerText} onChange={e => setFooterText(e.target.value)} className="rounded-lg border-white/10 bg-muted/30" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/70">Pie de página (icono URL)</Label>
                  <Input value={footerIcon} onChange={e => setFooterIcon(e.target.value)} placeholder="https://…" className="rounded-lg border-white/10 bg-muted/30" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground/70 cursor-pointer">Mostrar fecha/hora actual</Label>
                <Switch checked={withTimestamp} onCheckedChange={setWithTimestamp} />
              </div>

              {/* Fields */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60">Campos</Label>
                  <Button variant="outline" size="sm" onClick={addField} className="h-7 rounded-lg text-xs">
                    <Plus className="h-3.5 w-3.5" /> Añadir campo
                  </Button>
                </div>
                {fields.map((f, i) => (
                  <div key={i} className="space-y-2 rounded-lg border border-white/8 bg-muted/20 p-3">
                    <div className="flex items-center gap-2">
                      <Input value={f.name} onChange={e => updateField(i, { name: e.target.value })} placeholder="Nombre del campo" className="rounded-lg border-white/10 bg-muted/30 text-sm" />
                      <Button variant="ghost" size="icon" onClick={() => removeField(i)} className="h-8 w-8 flex-shrink-0 text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Textarea value={f.value} onChange={e => updateField(i, { value: e.target.value })} placeholder="Valor del campo" className="min-h-[52px] rounded-lg border-white/10 bg-muted/30 text-sm" />
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                      <Switch checked={f.inline} onCheckedChange={v => updateField(i, { inline: v })} /> En línea (inline)
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button onClick={handleSend} disabled={!canSend} className="w-full rounded-xl h-11">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Enviando…' : target === 'channel'
              ? `Enviar a ${selectedChannel ? `#${selectedChannel.name}` : 'canal'}`
              : 'Enviar DM'}
          </Button>
        </div>
      </ScrollArea>

      {/* ── Vista previa ── */}
      <div className="hidden lg:flex w-80 flex-shrink-0 flex-col">
        <Label className="mb-2 text-[0.62rem] font-bold uppercase tracking-widest text-muted-foreground/50">Vista previa</Label>
        <ScrollArea className="flex-1 min-h-0 rounded-2xl border border-white/8 bg-[#313338] p-4">
          <div className="space-y-1">
            {content.trim() && (
              <p className="whitespace-pre-wrap break-words text-sm text-[#dbdee1]">{content}</p>
            )}
            {previewEmbed && (
              <div
                className="mt-1 overflow-hidden rounded border-l-4 bg-[#2b2d31] p-3 text-[#dbdee1]"
                style={{ borderColor: color }}
              >
                {authorName.trim() && (
                  <div className="mb-1 flex items-center gap-1.5">
                    {authorIcon.trim() && <img src={authorIcon} alt="" className="h-5 w-5 rounded-full object-cover" />}
                    <span className="text-xs font-semibold">{authorName}</span>
                  </div>
                )}
                {title.trim() && (
                  <p className={cn('text-sm font-semibold', titleUrl.trim() && 'text-[#00a8fc]')}>{title}</p>
                )}
                {description.trim() && (
                  <p className="mt-1 whitespace-pre-wrap break-words text-xs text-[#b5bac1]">{description}</p>
                )}
                {fields.filter(f => f.name.trim() || f.value.trim()).length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {fields.filter(f => f.name.trim() || f.value.trim()).map((f, i) => (
                      <div key={i} className={cn(f.inline ? 'col-span-1' : 'col-span-2')}>
                        {f.name.trim() && <p className="text-[0.7rem] font-semibold">{f.name}</p>}
                        {f.value.trim() && <p className="text-[0.7rem] text-[#b5bac1] whitespace-pre-wrap break-words">{f.value}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {imageUrl.trim() && (
                  <img src={imageUrl} alt="" className="mt-2 max-h-40 w-full rounded object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
                {thumbnailUrl.trim() && (
                  <img src={thumbnailUrl} alt="" className="float-right ml-2 mt-1 h-16 w-16 rounded object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
                {footerText.trim() && (
                  <div className="mt-2 flex items-center gap-1.5 text-[0.65rem] text-[#949ba4]">
                    {footerIcon.trim() && <img src={footerIcon} alt="" className="h-4 w-4 rounded-full object-cover" />}
                    <span>{footerText}</span>
                  </div>
                )}
              </div>
            )}
            {!content.trim() && !previewEmbed && (
              <div className="flex flex-col items-center justify-center py-12 text-center text-[#949ba4]">
                <ImageIcon className="mb-2 h-8 w-8 opacity-30" />
                <p className="text-xs">El mensaje aparecerá aquí.</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
